import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ShopHeader } from "@/components/ShopHeader";
import { ShopFooter } from "@/components/ShopFooter";
import { StoreNav } from "@/components/StoreNav";
import { useLang } from "@/context/LanguageContext";
import { CaretRight, Newspaper, ArrowRight } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    axios.get(`${API}/shop/blog`)
      .then((r) => setPosts(r.data.posts || []))
      .catch(() => setPosts([]));
  }, []);

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { year: "numeric", month: "long", day: "numeric" }) : "");
  const title = (p) => (lang === "en" ? (p.title_en || p.title_fr) : (p.title_fr || p.title_en));
  const excerpt = (p) => (lang === "en" ? (p.excerpt_en || p.excerpt_fr) : (p.excerpt_fr || p.excerpt_en));

  return (
    <div className="min-h-screen bg-white">
      <ShopHeader />
      <div className="mx-auto max-w-[1280px] px-4 py-4"><StoreNav active="blog" /></div>

      <div className="mx-auto max-w-[1280px] px-4 pb-10">
        <div className="mb-8 flex items-center gap-1 text-sm text-muted-foreground">
          <button onClick={() => navigate("/")} className="hover:text-primary">{t.home}</button>
          <CaretRight size={12} weight="bold" />
          <span className="text-foreground">{t.blogHeroTitle}</span>
        </div>

        <h1 className="mb-8 font-display text-4xl font-black tracking-tight text-[#2e3a47]" data-testid="blog-title">{t.blogHeroTitle}</h1>

        {posts === null ? (
          <div className="py-20 text-center text-muted-foreground">…</div>
        ) : posts.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-[#f4f7f9] py-20 text-center" data-testid="blog-empty">
            <Newspaper size={48} weight="thin" className="mb-4 text-primary" />
            <p className="max-w-md text-[15px] text-muted-foreground">{t.blogEmpty}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-list">
            {posts.map((p) => (
              <article key={p.id} data-testid={`blog-card-${p.slug}`}
                onClick={() => navigate(`/dernieres-nouvelles/${p.slug}`)}
                className="group flex cursor-pointer flex-col overflow-hidden rounded-lg border border-border bg-white transition hover:shadow-lg">
                <div className="h-48 w-full overflow-hidden bg-[#eef2f4]">
                  {p.cover_image ? (
                    <img src={p.cover_image} alt={title(p)} className="h-full w-full object-cover transition duration-300 group-hover:scale-105" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center"><Newspaper size={40} weight="thin" className="text-primary/40" /></div>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <div className="mb-2 text-xs font-bold uppercase tracking-wide text-primary">{fmtDate(p.published_at)}</div>
                  <h2 className="mb-2 font-display text-lg font-black leading-snug tracking-tight text-[#2e3a47]">{title(p)}</h2>
                  <p className="mb-4 line-clamp-3 flex-1 text-sm leading-relaxed text-foreground/70">{excerpt(p)}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-bold text-primary">{t.blogReadMore} <ArrowRight size={14} weight="bold" className="transition group-hover:translate-x-1" /></span>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      <ShopFooter />
    </div>
  );
}
